<script lang="ts">
  import Layout from "$app/Layouts/Admin.svelte"
  import { formatDate, xhr, toast } from "$app/utils"
  import PreResponse from "./PreResponse.svelte"
  import PostResponse from "./PostResponse.svelte"

  let props = $props()

  let { alignment_test }: { alignment_test: AlignmentTestType } = props
</script>

<Layout {...props}>
  <span slot="title">View Alignment Test ID: {alignment_test.id}</span>

  <div class="flex justify-start">
    <a
      href="/admin/alignment_tests"
      class="hover:text-blue-500 hover:underline text-gray-700 text-lg"
      >Back to Alignment Tests Index</a
    >
  </div>

  <div class="mt-4 border border-gray-500 rounded-lg p-6 shadow bg-white">
    <div class="flex gap-3 py-4 items-center">
      <div>
        <img
          src={alignment_test.plan.logo_url}
          alt={alignment_test.plan.name}
          class="w-10 h-10 mx-auto"
        />
        {#if alignment_test.plan.logo_url.match(/^data:image/)}
          <div class="text-gray-300 text-center">No Logo</div>
        {/if}
      </div>
      <div class="px-4">
        <span class="label-color">PLAN NAME</span>
        <span class="text-lg font-bold" title="Name"
          >{alignment_test.plan.name}</span
        >
      </div>
      <div class="px-4">
        <span class="label-color">DATE CREATED</span>
        <span class="text-lg font-bold" title="Name"
          >{formatDate(alignment_test.created_at)}</span
        >
      </div>
      <div class="px-4">
        <span class="label-color">ALIGNMENT TEST STATUS:</span>
        <span class="text-lg font-bold" title="Status"
          >{alignment_test.status_title}</span
        >
      </div>
    </div>

    <div>
      <div class="grid grid-cols-4 gap-2 h-16">
        <div class="px-4">
          <span class="label-color">Pre-Alignment Invite Sent:</span>
          <span class="text-lg font-bold" title="Status">
            {#if alignment_test.pre_test_started_at}
              {formatDate(alignment_test.pre_test_started_at)}
            {:else}
              Not Sent
            {/if}
          </span>
        </div>
        <div class="px-4">
          <span class="label-color">Pre-Alignment Scores:</span>
          <span class="text-sm text-gray-700">Alignment: </span>
          <span class="text-lg font-bold" title="Status"
            >{alignment_test.prealign_score}</span
          >
          <span class="ml-3 text-sm text-gray-700">Agility: </span>
          <span class="text-lg font-bold" title="Status"
            >{alignment_test.prealign_agility}</span
          >
        </div>
        <div class="px-4">
          <span class="label-color">Post-Alignment Invite Sent:</span>
          <span class="text-lg font-bold" title="Status">
            {#if alignment_test.post_test_started_at}
              {formatDate(alignment_test.post_test_started_at)}
            {:else}
              Not Sent
            {/if}
          </span>
        </div>
        <div class="px-4">
          <span class="label-color">Post-Alignment Scores:</span>
          <span class="text-sm text-gray-700">Alignment: </span>
          <span class="text-lg font-bold" title="Status"
            >{alignment_test.postalign_score}</span
          >
          <span class="ml-3 text-sm text-gray-700">Agility: </span>
          <span class="text-lg font-bold" title="Status"
            >{alignment_test.postalign_agility}</span
          >
        </div>
      </div>
    </div>
  </div>

  <div class="mt-4 border border-gray-500 rounded-lg p-6 shadow bg-white">
    <h2 class="text-2xl font-bold mb-4">Pre-Alignment Test</h2>

    <h3 class="text-lg font-bold text-center mt-4">
      Invited User Response Status
    </h3>
    <ul
      class="mt-4 py-4 max-w-2xl mx-auto bg-zinc-50 border border-zinc-100 rounded shadow"
    >
      {#each alignment_test.alignment_test_responses.filter((response) => response.test_type == "pre") as response (response.id)}
        <li class="max-w-xl mx-auto grid grid-cols-4 items-center py-2 px-4">
          <img
            src={response.user.avatar_thumb_url}
            class="w-8 h-8 rounded-lg"
            alt={response.user.name}
          />
          <span class="text-gray-600">{response.user.name}</span>
          <div>
            <div
              class="ml-2 inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20"
            >
              Step {response.current_step}
            </div>
          </div>
          <div class="flex justify-end items-center">
            {#if response.timeout}
              <span
                class="text-red-600 ml-4 text-xs font-bold bg-red-100 p-0.5 rounded-sm"
                >TIMEOUT</span
              >
            {/if}
            <div
              class="ml-2 inline-flex items-center rounded-md bg-teal-50 px-2 py-1 text-xs font-medium text-teal-800 ring-1 ring-inset ring-teal-600/20"
            >
              {response.status_title}
            </div>
          </div>
        </li>
      {/each}
    </ul>

    <h3 class="text-lg font-bold text-center mt-8">User Responses</h3>

    <PreResponse {alignment_test} />
  </div>

  <div class="mt-4 border border-gray-500 rounded-lg p-6 shadow bg-white">
    <h2 class="text-2xl font-bold mb-4">Post-Alignment Test</h2>

    {#if alignment_test.post_test_status == "not_started" || alignment_test.post_test_status == "invite_post_alignment_responses"}
      <div class="mt-6 flex justify-center">
        <p
          class="py-2 px-6 border-amber-100 bg-amber-50 text-amber-600 border text-lg"
        >
          Post-Alignment Test has not yet started.
        </p>
      </div>
    {:else}
      <h3 class="text-lg font-bold text-center mt-4">
        Invited User Response Status
      </h3>
      <ul
        class="mt-4 py-4 max-w-2xl mx-auto bg-zinc-50 border border-zinc-100 rounded shadow"
      >
        {#each alignment_test.alignment_test_responses.filter((response) => response.test_type == "post") as response (response.id)}
          <li class="max-w-xl mx-auto grid grid-cols-4 items-center py-2 px-4">
            <img
              src={response.user.avatar_thumb_url}
              class="w-8 h-8 rounded-lg"
              alt={response.user.name}
            />
            <span class="text-gray-600">{response.user.name}</span>
            <div>
              <div
                class="ml-2 inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20"
              >
                Step {response.current_step}
              </div>
            </div>
            <div class="flex justify-end items-center">
              {#if response.timeout}
                <span
                  class="text-red-600 ml-4 text-xs font-bold bg-red-100 p-0.5 rounded-sm"
                  >TIMEOUT</span
                >
              {/if}
              <div
                class="ml-2 inline-flex items-center rounded-md bg-teal-50 px-2 py-1 text-xs font-medium text-teal-800 ring-1 ring-inset ring-teal-600/20"
              >
                {response.status_title}
              </div>
            </div>
          </li>
        {/each}
      </ul>

      <h3 class="text-lg font-bold text-center mt-8">User Responses</h3>

      <PostResponse {alignment_test} />
    {/if}
  </div>
</Layout>
